// src/pages/TicketDeliveryManagement/components/TicketDeliveryList.tsx
// Bảng danh sách phiếu giao vé đã lập + action Confirm/Cancel cho phiếu còn Draft

import React from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_DELIVERY_STATUS,
  TICKET_DELIVERY_STATUS_COLORS,
  TICKET_DELIVERY_STATUS_LABELS,
  TicketDelivery,
  TicketDeliveryPagination,
} from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

interface TicketDeliveryListProps {
  deliveries: TicketDelivery[];
  pagination: TicketDeliveryPagination;
  agentMap: AgentMap;
  loading: boolean;
  onViewDetail: (delivery: TicketDelivery) => void;
  onConfirm: (delivery: TicketDelivery) => void;
  onCancel: (delivery: TicketDelivery) => void;
  onAgentConfirm: (delivery: TicketDelivery) => void;
  highlightDeliveryId?: number | null;
}

// Tập action-code tĩnh của cột "Thao tác" — ẩn hẳn cột khi role hiện tại không có quyền nào
// Mục 8.b, README_UPDATE.md — gộp thêm "button-agent-confirm" (trước đây chỉ có ở màn TicketManagement
// riêng) vào đúng bảng này để 2 màn "Danh sách phiếu" và "Giao vé đại lý" dùng chung 1 bảng, phân biệt
// hành động theo trạng thái phiếu thay vì tách 2 trang.
const DELIVERY_ACTION_COLUMN_CODES = ['button-detail', 'button-confirm', 'button-cancel', 'button-agent-confirm'];

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

// HH bán hàng của phiếu = giá trị quy đổi phiếu × % HH SNAPSHOT / 100. Phiếu cũ chưa có snapshot rate → '-'.
const salesCommissionOf = (d: TicketDelivery): number | null =>
  typeof d.salesCommissionRate === 'number' && typeof d.estimatedValue === 'number'
    ? Math.round(d.estimatedValue * d.salesCommissionRate / 100)
    : null;

const TicketDeliveryList: React.FC<TicketDeliveryListProps> = ({
  deliveries,
  pagination,
  agentMap,
  loading,
  onViewDetail,
  onConfirm,
  onCancel,
  onAgentConfirm,
  highlightDeliveryId,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    DELIVERY_ACTION_COLUMN_CODES,
  );

  const columnCount = (canShowActionColumn ? 9 : 8) + 1;

  const renderStatusBadge = (delivery: TicketDelivery) => {
    const color =
      delivery.statusBadgeColor || TICKET_DELIVERY_STATUS_COLORS[String(delivery.status)] || 'secondary';
    const label =
      delivery.statusName || TICKET_DELIVERY_STATUS_LABELS[String(delivery.status)] || st('khong_ro_text');
    return <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>{label}</span>;
  };

  const renderActions = (delivery: TicketDelivery) => {
    const isDraft = Number(delivery.status) === TICKET_DELIVERY_STATUS.Draft;
    const isConfirmed = Number(delivery.status) === TICKET_DELIVERY_STATUS.Confirmed;

    return (
      <div className="d-flex justify-content-center gap-1 flex-nowrap">
        {PageActionPermission.canDisplay(location.pathname, 'button-detail') && (
          <Button
            color="info"
            outline
            size="sm"
            className="btn-icon"
            title={st('xem_chi_tiet_title')}
            data-action-code="button-detail"
            data-action-name="Xem chi tiết phiếu giao vé"
            onClick={() => onViewDetail(delivery)}
          >
            <i className="ri-eye-line"></i>
          </Button>
        )}

        {isDraft && PageActionPermission.canDisplay(location.pathname, 'button-confirm') && (
          <Button
            color="success"
            outline
            size="sm"
            className="btn-icon"
            title={st('xac_nhan_giao_ve_title')}
            data-action-code="button-confirm"
            data-action-name="Xác nhận giao vé"
            onClick={() => onConfirm(delivery)}
          >
            <i className="ri-check-line"></i>
          </Button>
        )}

        {isDraft && PageActionPermission.canDisplay(location.pathname, 'button-cancel') && (
          <Button
            color="danger"
            outline
            size="sm"
            className="btn-icon"
            title={st('huy_phieu_title')}
            data-action-code="button-cancel"
            data-action-name="Huỷ phiếu giao vé"
            onClick={() => onCancel(delivery)}
          >
            <i className="ri-close-line"></i>
          </Button>
        )}

        {isConfirmed && PageActionPermission.canDisplay(location.pathname, 'button-agent-confirm') && (
          <Button
            color="primary"
            outline
            size="sm"
            title={st('xac_nhan_dai_ly_da_nhan_title')}
            data-action-code="button-agent-confirm"
            data-action-name="Xác nhận đại lý đã nhận vé"
            onClick={() => onAgentConfirm(delivery)}
          >
            <i className="ri-check-double-line align-bottom me-1"></i>
            {st('xac_nhan_dai_ly_button')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
          <th>{st('ma_phieu_column')}</th>
          <th>{st('ke_hoach_phat_hanh_column')}</th>
          <th>{st('dai_ly_column')}</th>
          <th className="text-end">{st('so_luong_ve_column')}</th>
          <th className="text-end">{st('hh_ban_hang_column')}</th>
          <th>{st('trang_thai_column')}</th>
          <th>{st('nguoi_lap_column')}</th>
          <th>{st('ngay_giao_column')}</th>
          {canShowActionColumn && (
            <th style={{ width: 150 }} className="text-center">
              {st('thao_tac_column')}
            </th>
          )}
        </tr>
      }
    >
      {loading ? (
        Array.from({ length: 5 }).map((_, row) => (
          <tr key={row}>
            {Array.from({ length: columnCount }).map((__, col) => (
              <td key={col}>
                <div className="placeholder-glow">
                  <span className="placeholder col-8"></span>
                </div>
              </td>
            ))}
          </tr>
        ))
      ) : deliveries.length === 0 ? (
        <tr>
          <td colSpan={columnCount} className="text-center text-muted py-5">
            <div className="mb-2">
              <i className="ri-file-list-3-line fs-1 text-muted"></i>
            </div>
            {st('chua_co_phieu_giao_ve_nao_text')}
          </td>
        </tr>
      ) : (
        deliveries.map((delivery, index) => (
          <tr
            key={delivery.id}
            className={delivery.id === highlightDeliveryId ? 'table-warning' : undefined}
          >
            <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
            <td>
              <div className="fw-medium text-primary">{delivery.deliveryCode}</div>
            </td>
            <td>{delivery.planCode || delivery.issuancePlanId}</td>
            <td>
              <div className="fw-medium">{getAgentName(agentMap, delivery.agentId)}</div>
              <small className="text-muted">{getAgentCode(agentMap, delivery.agentId)}</small>
            </td>
            <td className="text-end">{formatNumber(delivery.totalQuantity)}</td>
            <td className="text-end">
              {typeof delivery.salesCommissionRate === 'number' ? (
                <>
                  {formatCurrency(salesCommissionOf(delivery))}
                  <small className="text-muted d-block">{delivery.salesCommissionRate}%</small>
                </>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td>{renderStatusBadge(delivery)}</td>
            <td>{delivery.deliveredByName || delivery.createdByName || '-'}</td>
            <td>{formatDate(delivery.deliveredAt || delivery.createdAt)}</td>
            {canShowActionColumn && <td className="text-center">{renderActions(delivery)}</td>}
          </tr>
        ))
      )}
    </DataTableShell>
  );
};

export default TicketDeliveryList;
